import { useSelector } from 'react-redux';

import { getSubFilterTypeSelector } from 'store/selectors/feed';
import { getFeedCriteriaSelectorV3 } from 'store/selectors/feedv3';
import { subFilterTypes } from 'settings/constants/properties';

import styles from './styles.module.scss';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { routes } from 'settings/navigation/routes';
import { getUserRolesMapSelector } from 'store/selectors/user';
import { isCassAddressSet } from 'helpers';

const EmptyStateIcon = () => (
  <svg
    width="100"
    height="100"
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle opacity="0.08" cx="50" cy="50" r="50" fill="#747475" fillOpacity="0.9" />
    <g opacity="0.4">
      <path
        d="M27.5 41V70.5C27.5 72.7091 29.2909 74.5 31.5 74.5H68.5C70.7091 74.5 72.5 72.7091 72.5 70.5V40.5L52.5746 24.1169C51.0831 22.8906 48.9286 22.9047 47.4534 24.1504L27.5 41Z"
        fill="white"
        stroke="#747475"
        strokeOpacity="0.9"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M42.4922 74.5V59C42.4922 57.8954 43.3876 57 44.4922 57H55.4922C56.5968 57 57.4922 57.8954 57.4922 59V74.5"
        stroke="#747475"
        strokeOpacity="0.9"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22.5 44.5L47.467 24.0724C48.9405 22.8669 51.0595 22.8669 52.533 24.0724L62.4939 32.2223M77.5 44.5L72.4939 40.4041M62.4939 32.2223V24C62.4939 22.8954 63.3893 22 64.4939 22H70.4939C71.5985 22 72.4939 22.8954 72.4939 24V40.4041M62.4939 32.2223L72.4939 40.4041"
        stroke="#747475"
        strokeOpacity="0.9"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.8117 8.17795C15.1526 7.4414 16.1826 7.39634 16.5866 8.10031L19.0232 12.3471C19.1263 12.5267 19.2826 12.6699 19.4706 12.7569L23.9139 14.8135C24.6504 15.1544 24.6955 16.1844 23.9915 16.5884L19.7447 19.025C19.5651 19.1281 19.4219 19.2844 19.3349 19.4724L17.2784 23.9157C16.9375 24.6522 15.9074 24.6973 15.5035 23.9933L13.0668 19.7465C12.9637 19.5669 12.8074 19.4237 12.6195 19.3367L8.17615 17.2802C7.4396 16.9393 7.39454 15.9092 8.09851 15.5053L12.3453 13.0686C12.5249 12.9655 12.6681 12.8092 12.7551 12.6213L14.8117 8.17795Z"
        fill="white"
        stroke="#747475"
        strokeOpacity="0.9"
        strokeWidth="4"
      />
      <path
        d="M86.4276 77.0023C86.8263 76.2954 87.8567 76.3328 88.203 77.0668L90.0129 80.9025C90.1013 81.0898 90.2456 81.245 90.426 81.3468L94.1202 83.4302C94.8272 83.8289 94.7897 84.8592 94.0557 85.2056L90.2201 87.0155C90.0328 87.1038 89.8775 87.2482 89.7758 87.4286L87.6923 91.1228C87.2937 91.8297 86.2633 91.7923 85.9169 91.0583L84.1071 87.2227C84.0187 87.0354 83.8743 86.8801 83.6939 86.7784L79.9997 84.6949C79.2928 84.2962 79.3302 83.2659 80.0642 82.9195L83.8999 81.1097C84.0872 81.0213 84.2424 80.8769 84.3442 80.6965L86.4276 77.0023Z"
        fill="white"
        stroke="#747475"
        strokeOpacity="0.9"
        strokeWidth="4"
      />
    </g>
  </svg>
);

export const EmptyState = ({ isSimpleHeaderTitleVisible }) => {
  const subFilterType = useSelector(getSubFilterTypeSelector);
  const { criteria } = useSelector(getFeedCriteriaSelectorV3);
  const history = useHistory();
  const currentPath = history.location.pathname;
  const { isAgent } = useSelector(getUserRolesMapSelector);

  const emptyMessage = useMemo(() => {
    if (isSimpleHeaderTitleVisible?.value) {
      return `It looks like you don't have any ${isSimpleHeaderTitleVisible?.text?.toLowerCase()} yet.`;
    }

    switch (subFilterType) {
      case subFilterTypes.ALL:
        return "We didn't find any properties for this search.";
      case subFilterTypes.NEW:
        return "We didn't find any new properties for this search.";
      case subFilterTypes.OPEN_HOUSES:
        return "We didn't find any open houses for this search.";
      case subFilterTypes.RECOMMENDED:
        return "We didn't any find any recommended properties for this search.";
      case subFilterTypes.STATUS_CHANGES:
        if (isAgent)
          return "We didn't find any properties with recent status changes for this search.";
        return "We didn't find any status changes for this search.";
      case subFilterTypes.PRICE_REDUCTIONS:
        return "We didn't find any price reductions for this search.";
      case subFilterTypes.BACK_ON_THE_MARKET:
        if (isAgent)
          return "We didn't find any properties that recently came back on the market for this search.";
        return "We didn't find any back on the market for this search.";
      case subFilterTypes.COMMENTS:
        return "We didn't find any properties with comments for this search.";
      default:
        return '';
    }
  }, []);

  const emptyTitle = useMemo(() => {
    if (isSimpleHeaderTitleVisible?.value) {
      return `No ${isSimpleHeaderTitleVisible?.text}.`;
    }

    switch (subFilterType) {
      case subFilterTypes.ALL:
        return 'No Properties';
      case subFilterTypes.NEW:
        return 'No New Properties';
      case subFilterTypes.OPEN_HOUSES:
        return 'No Open Houses';
      case subFilterTypes.RECOMMENDED:
        return 'No Recommended Properties';
      case subFilterTypes.STATUS_CHANGES:
        return 'No Status Changes';
      case subFilterTypes.PRICE_REDUCTIONS:
        return 'No Price Reductions';
      case subFilterTypes.BACK_ON_THE_MARKET:
        if (isAgent) return `No Properties "Back on the Market"`;
        return 'No Back on the Market Properties';
      case subFilterTypes.COMMENTS:
        return 'No Comments';
      default:
        return '';
    }
  }, []);

  return (
    <div className={styles.propertiesEmptyContainer}>
      {isCassAddressSet(criteria?.Locations) ? (
        <>
          <EmptyStateIcon />
          <p className={styles.subtitle} style={{ marginTop: 20 }}>
            No results found. We weren&apos;t able to find any data for this address.
            <br />
            Please try a different address.
          </p>
        </>
      ) : (
        <>
          <p className={styles.title}>{emptyTitle}</p>
          <p className={styles.subtitle}>{emptyMessage}</p>
        </>
      )}
    </div>
  );
};
